import { useMemo } from 'react'
import { BarController, BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js'
import { Chart } from 'react-chartjs-2'
import classNames from 'classnames'
import styles from './ScrollableBarChart.module.scss'
import { AXIS_CANVAS_OVERFLOW, BAR_SLOT_WIDTH, Y_AXIS_WIDTH } from './constants.ts'
import { ScrollableBarChartProps } from './types.ts'
import { getAxisData, getAxisOptions, getPlotOptions } from './utils.ts'
import { yScaleTitle } from 'components/charts/plugins.ts'

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, yScaleTitle)

const ScrollableBarChart = ({
  data,
  options,
  label,
  xAxisLabel,
  barSlotWidth = BAR_SLOT_WIDTH,
  yAxisWidth = Y_AXIS_WIDTH,
  className
}: ScrollableBarChartProps) => {
  // Memoized constants
  const plotOptions = useMemo(() => getPlotOptions(options), [options])
  const axisOptions = useMemo(() => getAxisOptions(options, yAxisWidth), [options, yAxisWidth])
  const axisData = useMemo(() => getAxisData(data), [data])

  // Constants
  const plotWidth = (data.labels?.length ?? 0) * barSlotWidth

  return (
    <figure className={classNames(styles.chart, className)} aria-label={label}>
      <div className={styles.scroller}>
        <div className={styles['y-axis']} style={{ width: yAxisWidth }} aria-hidden>
          <div className={styles['y-axis-canvas']} style={{ width: yAxisWidth + AXIS_CANVAS_OVERFLOW }}>
            <Chart type="bar" options={axisOptions} data={axisData} />
          </div>
        </div>

        <div className={styles.plot} style={{ minWidth: plotWidth }}>
          <Chart type="bar" options={plotOptions} data={data} />
        </div>
      </div>

      {!!xAxisLabel && (
        <figcaption className={styles['x-axis-label']} style={{ paddingLeft: yAxisWidth }}>
          {xAxisLabel}
        </figcaption>
      )}
    </figure>
  )
}

export default ScrollableBarChart
